/**
 * Extract base name from a name that might have "-Copy-X" suffix
 * Examples:
 * - "Company A" -> "Company A"
 * - "Company A-Copy-1" -> "Company A"
 * - "Company A-Copy-5" -> "Company A"
 */
const extractBaseName = (name: string): string => {
  if (!name) {
    return ""
  }

  const copyPattern = /-Copy-(\d+)$/
  return name.replace(copyPattern, "")
}

/**
 * Helper function to generate duplicate name with suffix based on existing names
 * This ensures no duplicate names even if some copies were deleted
 * Examples:
 * - generateDuplicateName("Company A", []) -> "Company A-Copy-1"
 * - generateDuplicateName("Company A", ["Company A-Copy-1"]) -> "Company A-Copy-2"
 * - generateDuplicateName("Company A-Copy-1", ["Company A-Copy-1", "Company A-Copy-3"]) -> "Company A-Copy-4"
 * - generateDuplicateName("Company A-Copy-2", ["Company A-Copy-1", "Company A-Copy-3"]) -> "Company A-Copy-4"
 */
export const generateDuplicateName = (originalName: string, existingNames: string[] = []): string => {
  if (!originalName) {
    return "-Copy-1"
  }

  // Extract base name (remove -Copy-X if exists)
  const baseName = extractBaseName(originalName)

  // Find all existing copy numbers for this base name
  const copyPattern = new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-Copy-(\\d+)$`)
  const existingCopyNumbers: number[] = []

  existingNames.forEach(name => {
    const match = name.match(copyPattern)
    if (match) {
      existingCopyNumbers.push(parseInt(match[1], 10))
    }
  })

  // Find the next available copy number (max + 1)
  let nextCopyNumber = 1
  if (existingCopyNumbers.length > 0) {
    const maxCopyNumber = Math.max(...existingCopyNumbers)
    nextCopyNumber = maxCopyNumber + 1
  }

  return `${baseName}-Copy-${nextCopyNumber}`
}

/**
 * Helper function to check if a list of names contains duplicates
 * and suggest the next available copy number
 */
export const getNextAvailableCopyName = (baseName: string, existingNames: string[]): string => {
  let copyNumber = 1
  let candidateName = `${baseName}-Copy-${copyNumber}`

  // Keep incrementing until we find a name that doesn't exist
  while (existingNames.includes(candidateName)) {
    copyNumber++
    candidateName = `${baseName}-Copy-${copyNumber}`
  }

  return candidateName
}
